import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  Tooltip,
  useDisclosure,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback } from 'react';

import { Util } from '@finley-technologies/finley-types';

import { AthenaSchemaSidebar } from 'packages/athena/ui/AthenaSchemaSidebar';
import {
  QueryStringContainer,
  SaveQueryStringButton,
} from 'packages/athena/ui/QueryStringContainer';
import { colors } from 'packages/common-frontend/colors';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { FinleyAiAthenaSqlGenerationDrawerContainer } from 'packages/finleyAi/ui/FinleyAiAthenaSqlGenerationDrawerContainer';

type AthenaQueryStringWithSchemaSidebarContainerProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  changeQueryString: (params: {
    newQueryString: string;
    queryInputDateForColumnUpdate: Util.FullDateStr;
  }) => Promise<void>;
  isQueryStringSaveLoading?: boolean;
  initialQueryString: string;
  queryStringState: string;
  setQueryStringState: React.Dispatch<React.SetStateAction<string>>;
  editModeDisclosure: UseDisclosureReturn;
  selectedInputDate?: Util.FullDateStr;
  organizationId?: string;
};

export const AthenaQueryStringWithSchemaSidebarContainer = ({
  wipConfig,
  fullPropertyKey,
  changeQueryString,
  isQueryStringSaveLoading = false,
  initialQueryString,
  queryStringState,
  setQueryStringState,
  editModeDisclosure,
  selectedInputDate,
  organizationId,
}: AthenaQueryStringWithSchemaSidebarContainerProps) => {
  const {
    isOpen: isEditModeOpen,
    onOpen: onEditModeOpen,
    onClose: onEditModeClose,
  } = editModeDisclosure;

  const {
    isOpen: isFinleyAiDrawerOpen,
    onOpen: onFinleyAiDrawerOpen,
    onClose: onFinleyAiDrawerClose,
  } = useDisclosure();

  const hasUnsavedChanges =
    Boolean(queryStringState !== initialQueryString) && isEditModeOpen;
  const isClientWipMode = wipConfig?.operationMode === 'client';
  const creditFacilityId = getIn(
    wipConfig?.formProps.values,
    'creditFacilityId',
  );

  const onUpdateSqlByFinleyAi: (sqlString: string) => void = useCallback(
    (sqlString: string) => {
      if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
        setQueryStringState(sqlString);
        onFinleyAiDrawerClose();
        onEditModeOpen();
      }
    },
    [
      wipConfig,
      setQueryStringState,
      onFinleyAiDrawerClose,
      onEditModeOpen,
      fullPropertyKey,
    ],
  );

  return (
    <Box
      p="32px"
      backgroundColor={
        (isEditModeOpen || isQueryStringSaveLoading) && isClientWipMode
          ? colors.background[50]
          : undefined
      }
      borderColor={colors.gray[200]}
      borderWidth="1px"
      borderRadius="8px"
    >
      {hasUnsavedChanges && (
        <Alert status="warning" fontWeight={600} mb="32px">
          <AlertIcon boxSize="14px" />
          There are unsaved changes.
        </Alert>
      )}

      <Flex alignItems="center" justifyContent="space-between" mb="24px">
        <Flex alignItems="center">
          <Text mr="8px" fontWeight={600} fontSize="20px" minW="fit-content">
            SQL Query
          </Text>
          {wipConfig?.isMutateAllowed(fullPropertyKey) &&
            _.isString(creditFacilityId) && (
              <>
                <Tooltip label="Finley AI">
                  <Button
                    w="40px"
                    variant="ghost"
                    onClick={onFinleyAiDrawerOpen}
                    isDisabled={isQueryStringSaveLoading}
                  >
                    <Icon icon="wand-magic-sparkles" />
                  </Button>
                </Tooltip>
                <FinleyAiAthenaSqlGenerationDrawerContainer
                  isOpen={isFinleyAiDrawerOpen}
                  onClose={onFinleyAiDrawerClose}
                  creditFacilityId={creditFacilityId}
                  organizationId={organizationId}
                  onClickSqlAction={{
                    callback: onUpdateSqlByFinleyAi,
                    label: 'Update SQL Input',
                  }}
                />
              </>
            )}
        </Flex>
        {(isEditModeOpen || isQueryStringSaveLoading) &&
        wipConfig?.isMutateAllowed(fullPropertyKey) &&
        _.isString(creditFacilityId) ? (
          <Box>
            <Button
              colorScheme="aurora"
              variant="outline"
              borderWidth="2px"
              mr="24px"
              onClick={() => {
                onEditModeClose();
                setQueryStringState(initialQueryString);
              }}
              isDisabled={isQueryStringSaveLoading}
              backgroundColor={isClientWipMode ? 'white' : undefined}
            >
              Cancel
            </Button>
            <SaveQueryStringButton
              newQueryString={queryStringState}
              changeQueryString={changeQueryString}
              isQueryStringSaveLoading={isQueryStringSaveLoading}
              selectedInputDate={selectedInputDate}
              onEditModeClose={onEditModeClose}
            />
          </Box>
        ) : (
          <EditIconButton
            ariaLabel="edit sql query"
            label="Edit SQL Query"
            onClick={onEditModeOpen}
            isDisabled={isQueryStringSaveLoading}
          />
        )}
      </Flex>
      {/* *** SQL Query Management *** */}
      <Grid templateColumns="repeat(4, 1fr)">
        <GridItem colSpan={3}>
          <QueryStringContainer
            wipConfig={wipConfig}
            fullPropertyKey={`${fullPropertyKey}.query.queryString`}
            queryStringState={queryStringState}
            setQueryStringState={setQueryStringState}
            editModeState={{
              isEditModeOpen,
              onEditModeOpen,
              onEditModeClose,
            }}
          />
        </GridItem>
        {/* *** Athena Schema Sidebar *** */}
        {wipConfig?.isMutateAllowed(fullPropertyKey) &&
          _.isString(creditFacilityId) && (
            <GridItem
              colSpan={1}
              pl="24px"
              pt={isClientWipMode ? undefined : '44px'}
            >
              <AthenaSchemaSidebar
                creditFacilityId={creditFacilityId}
                isLenderWipMode={isClientWipMode}
                organizationId={organizationId}
              />
            </GridItem>
          )}
      </Grid>
    </Box>
  );
};
